import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import {
  CellDef,
  SearchOnEntitySearchCell,
} from '../../../../../containers/SingleSheetV2/table/model'
import { SelectOption } from '../../../../../model/selectOption'
import {
  PROJECT_CODE_MAX_LENGTH,
  PROJECT_DISPLAY_NAME_MAX_LENGTH,
  PROJECT_MAIN_EMAIL_MAX_LENGTH,
  PROJECT_OFFICIAL_NAME_MAX_LENGTH,
} from '../../../../../../domain/entity/ProjectEntity'
import { AllState } from '../../../../../../store'

// MIMEタイプではなく拡張子で指定する（FileUploadDialog が mime.getType で変換するため）。
const ICON_FILE_FORMAT = '.gif,.png,.jpg,.jpeg'

export type ProjectCellOptions = {
  searchDivision: SearchOnEntitySearchCell
  searchBusinessClient: SearchOnEntitySearchCell
  searchProjectManager: SearchOnEntitySearchCell
  statusOptions: SelectOption[]
  wbsItemCodeFormatOptions: SelectOption[]
}

// code / status / wbsItemCodeFormat は画面（登録/情報・作成/更新）で編集可否が異なる
// ため、呼び出し側から渡す。それ以外の編集可否は固定。
export type ProjectCellEditability = {
  code: boolean
  status: boolean
  wbsItemCodeFormat: boolean
}

export type ProjectCellDefs = {
  code: CellDef
  iconUrl: CellDef
  displayName: CellDef
  officialName: CellDef
  businessClient: CellDef
  division: CellDef
  projectManager: CellDef
  status: CellDef
  scheduledDate: CellDef
  wbsItemCodeFormat: CellDef
  mainEmail: CellDef
  githubReviewerSyncEnabled: CellDef
}

// プロジェクト単票の全項目セル定義。行構成・どのセルを使うかは各 table-def 側で行う。
export const useProjectCellDefs = (
  options: ProjectCellOptions,
  editability: ProjectCellEditability
): ProjectCellDefs => {
  const intl = useIntl()
  const {
    searchDivision,
    searchBusinessClient,
    searchProjectManager,
    statusOptions,
    wbsItemCodeFormatOptions,
  } = options
  const {
    code: codeEditable,
    status: statusEditable,
    wbsItemCodeFormat: wbsItemCodeFormatEditable,
  } = editability
  // 代表メールの許可ドメイン（テナント設定）。カンマ区切り文字列を配列化する。
  const emailDomainWhitelistRaw = useSelector(
    (state: AllState) => state.tenant.organization?.emailDomainWhitelist
  )
  const emailDomainWhitelist = useMemo(
    () =>
      (emailDomainWhitelistRaw ?? '')
        .split(',')
        .map(domain => domain.trim())
        .filter(Boolean),
    [emailDomainWhitelistRaw]
  )
  return useMemo<ProjectCellDefs>(() => {
    const label = (id: string) => intl.formatMessage({ id })
    return {
      code: {
        externalId: 'project.code',
        field: 'code',
        label: label('projectV2.code'),
        type: 'TEXT',
        width: 'full',
        required: false,
        editable: codeEditable,
        placeholder: label('projectV2.code.placeholder'),
        maxLength: PROJECT_CODE_MAX_LENGTH,
      },
      iconUrl: {
        externalId: 'project.iconUrl',
        field: 'iconUrl',
        label: label('projectV2.iconUrl'),
        type: 'ICON',
        width: 'full',
        required: false,
        editable: true,
        fileFormat: ICON_FILE_FORMAT,
      },
      displayName: {
        externalId: 'project.displayName',
        field: 'displayName',
        label: label('projectV2.displayName'),
        type: 'TEXT',
        width: 'full',
        required: true,
        editable: true,
        maxLength: PROJECT_DISPLAY_NAME_MAX_LENGTH,
      },
      officialName: {
        externalId: 'project.officialName',
        field: 'officialName',
        label: label('projectV2.officialName'),
        type: 'TEXT',
        width: 'full',
        required: true,
        editable: true,
        maxLength: PROJECT_OFFICIAL_NAME_MAX_LENGTH,
      },
      businessClient: {
        externalId: 'project.businessClient',
        field: 'businessClient',
        label: label('projectV2.businessClient'),
        type: 'ENTITY_SEARCH',
        width: 'full',
        required: true,
        editable: true,
        search: searchBusinessClient,
        displayIcon: false,
      },
      division: {
        externalId: 'project.division',
        field: 'division',
        label: label('projectV2.division'),
        type: 'ENTITY_SEARCH',
        width: 'full',
        required: true,
        editable: true,
        search: searchDivision,
        displayIcon: false,
      },
      projectManager: {
        externalId: 'project.projectManager',
        field: 'projectManager',
        label: label('projectV2.projectManager'),
        type: 'ENTITY_SEARCH',
        width: 'full',
        required: true,
        editable: true,
        search: searchProjectManager,
        displayIcon: true,
      },
      status: {
        externalId: 'project.status',
        field: 'status',
        label: label('projectV2.status'),
        type: 'SELECT',
        width: 'full',
        required: true,
        editable: statusEditable,
        options: statusOptions,
        clearable: false,
      },
      scheduledDate: {
        externalId: 'project.scheduledDate',
        field: 'scheduledDate',
        label: label('projectV2.scheduledDate'),
        type: 'DATE_TERM',
        width: 'full',
        required: true,
        editable: true,
      },
      wbsItemCodeFormat: {
        externalId: 'project.wbsItemCodeFormat',
        field: 'wbsItemCodeFormat',
        label: label('projectV2.wbsItemCodeFormat'),
        type: 'SELECT',
        width: 'full',
        required: true,
        editable: wbsItemCodeFormatEditable,
        options: wbsItemCodeFormatOptions,
        clearable: false,
        help: label('projectV2.wbsItemCodeFormat.help'),
      },
      mainEmail: {
        externalId: 'project.mainEmail',
        field: 'mainEmail',
        label: label('projectV2.mainEmail'),
        type: 'EMAIL',
        width: 'full',
        required: false,
        editable: true,
        placeholder: label('projectV2.mainEmail.placeholder'),
        maxLength: PROJECT_MAIN_EMAIL_MAX_LENGTH,
        emailDomainWhitelist,
      },
      githubReviewerSyncEnabled: {
        externalId: 'project.githubReviewerSyncEnabled',
        field: 'githubReviewerSyncEnabled',
        label: label('projectV2.githubReviewerSyncEnabled'),
        type: 'CHECKBOX',
        width: 'full',
        required: false,
        editable: true,
      },
    }
  }, [
    intl,
    searchDivision,
    searchBusinessClient,
    searchProjectManager,
    statusOptions,
    wbsItemCodeFormatOptions,
    codeEditable,
    statusEditable,
    wbsItemCodeFormatEditable,
    emailDomainWhitelist,
  ])
}

export const PROJECT_BASIC_INFORMATION_HEADER_ID = 'basicInformation'
